import { tapBindingsCollide } from '@electron/shared/keyboard-accelerator';
import { config, useUserConfig } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import hummingbirdMeetingNotesVideo from '@/assets/video/hb-mn-demo.mp4';
import hummingbirdDemoVideo from '@/assets/video/hb-summarise-demo.mp4';
import hummingbirdTextSelectionVideo from '@/assets/video/hb-text-selection-demo.mp4';
import { XStack } from '@/components/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { IconButton } from '@/components/ui/IconButton';
import { KeyboardShortcutInput } from '@/components/ui/KeyboardShortcutInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';

import { SettingsSection } from '../settings-section';

const HUMMINGBIRD_FEATURES = [
  {
    id: 'screen-context',
    label: 'Any app',
    video: hummingbirdDemoVideo,
  },
  {
    id: 'meeting-notes',
    label: 'Ongoing meeting',
    video: hummingbirdMeetingNotesVideo,
  },
  {
    id: 'text-selection',
    label: 'Selected text',
    video: hummingbirdTextSelectionVideo,
  },
] as const;

type FeatureId = (typeof HUMMINGBIRD_FEATURES)[number]['id'];

function FeatureVideo({ featureId }: { featureId: FeatureId }) {
  const feature = HUMMINGBIRD_FEATURES.find((f) => f.id === featureId);
  const videoRef = useRef<HTMLVideoElement>(null);

  if (!feature) return null;

  const handleFullscreen = () => {
    if (videoRef.current) {
      videoRef.current.requestFullscreen();
    }
  };

  return (
    <div className="group relative">
      <video
        ref={videoRef}
        key={featureId}
        autoPlay
        loop
        muted
        playsInline
        className="w-full object-contain"
      >
        <source src={feature.video} type="video/mp4" />
      </video>
      <button
        onClick={handleFullscreen}
        className="absolute bottom-3 right-3 p-1.5 rounded-md bg-black/60 text-white/80 opacity-0 touch:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity hover:bg-black/80 hover:text-white"
      >
        <Icons.expand className="size-4" />
      </button>
    </div>
  );
}

const HB_DEMO_DISMISSED_KEY = 'hb-demo-dismissed';
const HB_NOTE_DISMISSED_KEY = 'hb-note-dismissed';

function hummingbirdShortcutRejection(shortcut: string): string | null {
  if (!tapBindingsCollide(shortcut, settingsStore.dictationShortcut)) {
    return null;
  }
  return "Dictation's shortcut is that key. Pick a different one.";
}

export const HummingbirdSettingsSection = observer(() => {
  const [activeFeature, setActiveFeature] =
    useState<FeatureId>('screen-context');
  const [demoOpen, setDemoOpen] = useState(
    () => localStorage.getItem(HB_DEMO_DISMISSED_KEY) !== 'true'
  );
  const [noteDismissed, setNoteDismissed] = useState(
    () => localStorage.getItem(HB_NOTE_DISMISSED_KEY) === 'true'
  );

  const handleDemoOpenChange = useCallback((open: boolean) => {
    setDemoOpen(open);
    if (!open && settingsStore.hummingbirdEnabled) {
      localStorage.setItem(HB_DEMO_DISMISSED_KEY, 'true');
    }
  }, []);

  const handleDismissNote = useCallback(() => {
    setNoteDismissed(true);
    localStorage.setItem(HB_NOTE_DISMISSED_KEY, 'true');
  }, []);

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Hover</SettingsSection.Title>
        <SettingsSection.Description>
          Chat with anything on your screen — apps, meetings, or selected text.
        </SettingsSection.Description>
      </SettingsSection.Header>
      <Collapsible open={demoOpen} onOpenChange={handleDemoOpenChange}>
        <div className="rounded-lg border border-border-strong bg-surface overflow-hidden">
          <CollapsibleTrigger className="flex w-full items-center gap-1.5 px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors">
            <Icons.chevronRight className="size-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-90 [[data-state=open]>&]:rotate-90" />
            See in action
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Tabs
              value={activeFeature}
              onValueChange={(v) => setActiveFeature(v as FeatureId)}
            >
              <TabsList className="ml-2">
                {HUMMINGBIRD_FEATURES.map((feature) => (
                  <TabsTrigger key={feature.id} value={feature.id}>
                    {feature.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {HUMMINGBIRD_FEATURES.map((feature) => (
                <TabsContent
                  key={feature.id}
                  value={feature.id}
                  className="mt-0"
                >
                  <FeatureVideo featureId={feature.id} />
                </TabsContent>
              ))}
            </Tabs>
          </CollapsibleContent>
        </div>
      </Collapsible>
      <SettingsSection.Group>
        <HummingbirdSettings />
      </SettingsSection.Group>
      {!noteDismissed && (
        <XStack className="items-start gap-3 rounded-lg border border-border-strong bg-surface py-3 pl-4 pr-3">
          <Icons.info className="size-4 text-foreground-muted shrink-0 mt-0.5" />
          <span className="flex-1 text-sm text-foreground-subtle">
            Hover only looks at your screen when you ask it to. Because you're
            choosing to share that moment, it sees everything, including apps
            and websites you've excluded in Settings {'>'} Privacy Controls.
          </span>
          <XStack className="-mt-0.5">
            <IconButton
              icon={Icons.cancel}
              size="xs"
              variant="ghost"
              tooltip="Dismiss"
              aria-label="Dismiss"
              onClick={handleDismissNote}
            />
          </XStack>
        </XStack>
      )}
    </SettingsSection.Root>
  );
});

export const HummingbirdSettings = observer(
  ({ isCompact = false }: { isCompact?: boolean }) => {
    const userConfig = useUserConfig();
    return (
      <>
        <SettingsSection.Item searchId="hummingbird.enable">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              <XStack
                className={cn('items-center gap-2', isCompact && 'text-base')}
              >
                Enable Hover
              </XStack>
            </SettingsSection.ItemLabel>
            {!isCompact && (
              <SettingsSection.ItemDescription>
                Open Littlebird from anywhere and ask about what's on your
                screen.
              </SettingsSection.ItemDescription>
            )}
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={settingsStore.hummingbirdEnabled}
            onCheckedChange={(v) => {
              trackEvent('HUMMINGBIRD_SETTING_TOGGLED', {
                enabled: v,
                isCompact,
              });
              settingsStore.setHummingbirdEnabled(v);
            }}
          />
        </SettingsSection.Item>
        {settingsStore.hummingbirdEnabled && (
          <>
            <SettingsSection.Item searchId="hummingbird.global-shortcut">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  Global Shortcut
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Choose the global shortcut used to open Hover
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <KeyboardShortcutInput
                value={settingsStore.hummingbirdShortcut}
                onChange={(v) => settingsStore.setHummingbirdShortcut(v)}
                disabled={!settingsStore.hummingbirdEnabled}
                validate={hummingbirdShortcutRejection}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.voice-shortcut">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  Voice Shortcut
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Speak to Littlebird from anywhere on your desktop.
                </SettingsSection.ItemDescription>
                <SettingsSection.ItemDescription>
                  Tap Option once, then hold to speak. Release when done.
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                checked={settingsStore.voiceShortcutEnabled}
                onCheckedChange={(v) => {
                  settingsStore.setVoiceShortcutEnabled(v);
                  trackEvent('HUMMINGBIRD_VOICE_SHORTCUT_TOGGLED', {
                    enabled: v,
                  });
                }}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.auto-send-voice">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  Auto-Send Voice Messages
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Send your message as soon as the transcription finishes. Turn
                  off to review the transcript before sending.
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                checked={settingsStore.voiceAutoSendEnabled}
                disabled={!settingsStore.voiceShortcutEnabled}
                onCheckedChange={(v) => {
                  settingsStore.setVoiceAutoSendEnabled(v);
                  trackEvent('HUMMINGBIRD_VOICE_AUTO_SEND_TOGGLED', {
                    enabled: v,
                  });
                }}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.always-new-chat">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  Always Start New Chat
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Start with a fresh chat each time you open Hover
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                checked={config.chat.hummingbird_always_new_chat}
                onCheckedChange={(v) => {
                  userConfig.chat.set('hummingbird_always_new_chat', v);
                }}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.front-app-context">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel id="hummingbird-front-app-context">
                  Include Front App Context
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Include context from the frontmost app when Hover opens to a
                  new chat.
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                id="hummingbird-front-app-context"
                checked={settingsStore.hummingbirdFrontAppContextEnabled}
                onCheckedChange={(v) => {
                  settingsStore.setHummingbirdFrontAppContextEnabled(v);
                  trackEvent('HUMMINGBIRD_FRONT_APP_CONTEXT_SETTING_TOGGLED', {
                    enabled: v,
                  });
                }}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.screenshot-fallback">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel id="hummingbird-screenshot-fallback">
                  Screenshot Unreadable Apps
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Attach a screenshot of a mentioned app when Hover cannot read
                  its text, instead of asking you to take one.
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                id="hummingbird-screenshot-fallback"
                checked={settingsStore.hummingbirdScreenshotFallbackEnabled}
                onCheckedChange={(v) => {
                  settingsStore.setHummingbirdScreenshotFallbackEnabled(v);
                  trackEvent(
                    'HUMMINGBIRD_SCREENSHOT_FALLBACK_SETTING_TOGGLED',
                    {
                      enabled: v,
                    }
                  );
                }}
              />
            </SettingsSection.Item>
            <SettingsSection.Item searchId="hummingbird.hide-screen-sharing">
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>
                  Hide From Screen Sharing
                </SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  Keep Hover invisible to screen capture and shared screens
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <SettingsSection.Switch
                checked={settingsStore.hummingbirdHideFromScreenShare}
                onCheckedChange={(v) => {
                  settingsStore.setHummingbirdHideFromScreenShare(v);
                }}
              />
            </SettingsSection.Item>
          </>
        )}
      </>
    );
  }
);
